import { track, setUserId, identify, Identify, reset } from '@amplitude/unified';
import { useLogin } from '../hooks/use_backend';
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export const getUserRole = (login) => {
  if (!login || !login.privileges) return 'unknown';

  const features = login.privileges.map(p => p.feature);

  if (features.includes('admin')) {
    return 'admin';
  } else if (features.includes('commissioningprocedure')) {
    return 'installer';
  }

  return 'user';
};

export const useUserRole = () => {
  const login = useLogin();
  return useMemo(() => getUserRole(login), [login]);
};

export const useAmplitudeTrack = () => {
  const userRole = useUserRole();
  // The role is read through a ref so the returned functions keep a stable identity: a role change
  // (e.g. unknown -> installer after login) must not re-fire effects that list them as dependencies.
  const userRoleRef = useRef(userRole);
  userRoleRef.current = userRole;

  return useMemo(() => ({
    trackEvent: (eventName, properties = {}) => trackEvent(eventName, { user_role: userRoleRef.current, ...properties }),
    trackSiteTabViewed: (tabName, properties = {}) => trackSiteTabViewed(tabName, { user_role: userRoleRef.current, ...properties }),
    trackPageLoaded: (pageName, loadTimeMs, properties = {}) => trackPageLoaded(pageName, loadTimeMs, { user_role: userRoleRef.current, ...properties }),
    trackSiteTabLoaded: (tabName, loadTimeMs, properties = {}) => trackSiteTabLoaded(tabName, loadTimeMs, { user_role: userRoleRef.current, ...properties }),
    trackButtonClicked: (buttonName, buttonLocation, properties = {}) => trackButtonClicked(buttonName, buttonLocation, { user_role: userRoleRef.current, ...properties }),
  }), []);
};

let identifiedUserId = null;
let identifiedUserRole = null;

// Sync Amplitude identity with the login state. Safe to call repeatedly (e.g. on every token refresh):
// it only talks to Amplitude when the user or role actually changed, and resets the identity on logout.
export const initializeUserTracking = (login) => {
  try {
    if (!login || !login.username) {
      if (identifiedUserId !== null) {
        reset();
        identifiedUserId = null;
        identifiedUserRole = null;
      }
      return;
    }

    const userId = login.username;
    const userRole = getUserRole(login);

    if (userId === identifiedUserId && userRole === identifiedUserRole) return;

    // Switching directly between users without logout: start from a clean identity
    if (identifiedUserId !== null && userId !== identifiedUserId) {
      reset();
    }

    setUserId(userId);

    const identifyEvent = new Identify();
    identifyEvent.set('user_role', userRole);
    identifyEvent.set('has_privileges', !!(login.privileges && login.privileges.length > 0));
    identifyEvent.set('privilege_count', login.privileges ? login.privileges.length : 0);
    identify(identifyEvent);

    identifiedUserId = userId;
    identifiedUserRole = userRole;
    console.log(`[amplitude] Logged in as "${userId}" with role "${userRole}"`);
  } catch (error) {
    console.error('[amplitude] Error initializing user tracking:', error);
  }
};

// mobile / tablet / desktop, to explain slow flows on site (own name: Amplitude already uses "device_type")
const getDeviceCategory = () => {
  try {
    const ua = navigator.userAgent || '';
    const isIpadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    if (isIpadOs || /iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
    return 'desktop';
  } catch (error) {
    return 'unknown';
  }
};

// Effective connection type ('slow-2g' | '2g' | '3g' | '4g'). Only exposed by Chromium browsers.
const getNetworkType = () => {
  try {
    return navigator.connection?.effectiveType;
  } catch (error) {
    return undefined;
  }
};

// Number of sites in the user's sites list, remembered once the sites overview has loaded
let sitesCount = null;
export const setSitesCount = (count) => {
  sitesCount = count;
};
export const getSitesCount = () => sitesCount;

export const trackEvent = (eventName, properties = {}) => {
  const networkType = getNetworkType();
  const eventProps = {
    timestamp: new Date().toISOString(),
    device_category: getDeviceCategory(),
    ...(networkType ? { network_type: networkType } : {}),
  };

  Object.keys(properties).forEach(key => {
    const value = properties[key];
    if (typeof value === 'number') {
      eventProps[key] = value;
    } else if (typeof value === 'string' && /^\d+$/.test(value)) {
      eventProps[key] = parseInt(value, 10);
    } else {
      eventProps[key] = value;
    }
  });

  track(eventName, eventProps);
};

// Naming convention: snake_case events as "<object>_<past-tense verb>", snake_case properties.
// Every event also gets user_role (via useAmplitudeTrack) and, where relevant, site_id.

// One explicit event per site tab, so each tab can be selected and filtered by event name.
const SITE_TAB_EVENTS = Object.freeze({
  general: { viewed: 'site_tab_general_viewed', loaded: 'site_tab_general_loaded' },
  devices: { viewed: 'site_tab_devices_viewed', loaded: 'site_tab_devices_loaded' },
  installation: { viewed: 'site_tab_installation_viewed', loaded: 'site_tab_installation_loaded' },
  schematic: { viewed: 'site_tab_schematic_viewed', loaded: 'site_tab_schematic_loaded' },
  metering: { viewed: 'site_tab_metering_viewed', loaded: 'site_tab_metering_loaded' },
  log: { viewed: 'site_tab_log_viewed', loaded: 'site_tab_log_loaded' },
  commissioning: { viewed: 'site_tab_commissioning_viewed', loaded: 'site_tab_commissioning_loaded' },
  alerts: { viewed: 'site_tab_alerts_viewed', loaded: 'site_tab_alerts_loaded' },
  statistics: { viewed: 'site_tab_statistics_viewed', loaded: 'site_tab_statistics_loaded' },
  financial: { viewed: 'site_tab_financial_viewed', loaded: 'site_tab_financial_loaded' },
});

// A tab of the site detail page was opened
export const trackSiteTabViewed = (tabName, properties = {}) => {
  if (!SITE_TAB_EVENTS[tabName]) return;
  trackEvent(SITE_TAB_EVENTS[tabName].viewed, {
    tab_name: tabName,
    ...properties,
  });
};

// A tab of the site detail page finished loading its site data
export const trackSiteTabLoaded = (tabName, loadTimeMs, properties = {}) => {
  if (!SITE_TAB_EVENTS[tabName]) return;
  trackEvent(SITE_TAB_EVENTS[tabName].loaded, {
    tab_name: tabName,
    load_time_ms: Math.round(loadTimeMs),
    ...properties,
  });
};

// "Site-contract management" -> "site_contract_management", for use in event and button names
export const toSnakeCase = (text) => String(text).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// A button or link was clicked. One explicit event per button ("button_<name>_clicked") so a Journeys/Pathfinder
// chart shows every button as its own step between the page and tab events; button_name and button_location
// (the page/section it sits in) stay available as properties for filtering and grouping.
export const trackButtonClicked = (buttonName, buttonLocation, properties = {}) => {
  trackEvent(`button_${toSnakeCase(buttonName)}_clicked`, {
    button_name: buttonName,
    button_location: buttonLocation,
    ...properties,
  });
};

// A page finished loading its main data
export const trackPageLoaded = (pageName, loadTimeMs, properties = {}) => {
  trackEvent('page_loaded', {
    page_name: pageName,
    load_time_ms: Math.round(loadTimeMs),
    ...properties,
  });
};

// Where the user came from when opening a site detail page. Set right before navigating,
// consumed once by SitePage. When nothing is set, the caller passes a fallback derived from the router.
export const SITE_SELECTION_SOURCE = Object.freeze({
  SITES_OVERVIEW: 'sites_overview',
  FAVOURITE_SITES_CARD: 'favourite_sites_card',
  SITE_ACTIVITY_CARD: 'site_activity_card',
  USER_ACTIVITY_CARD: 'user_activity_card',
  STATISTICS_CARD: 'statistics_card',
  REVIEW_TIMELINE: 'review_timeline',
  PAGE_LOAD: 'page_load',
  BROWSER_HISTORY: 'browser_history',
  OTHER: 'other',
});

// One explicit event per way of opening a site, so each can be selected and filtered by event name.
const SITE_SELECTION_EVENTS = Object.freeze({
  [SITE_SELECTION_SOURCE.SITES_OVERVIEW]: 'site_selected_via_sites_overview',
  [SITE_SELECTION_SOURCE.FAVOURITE_SITES_CARD]: 'site_selected_via_favourite_sites_card',
  [SITE_SELECTION_SOURCE.SITE_ACTIVITY_CARD]: 'site_selected_via_site_activity_card',
  [SITE_SELECTION_SOURCE.USER_ACTIVITY_CARD]: 'site_selected_via_user_activity_card',
  [SITE_SELECTION_SOURCE.STATISTICS_CARD]: 'site_selected_via_statistics_card',
  [SITE_SELECTION_SOURCE.REVIEW_TIMELINE]: 'site_selected_via_review_timeline',
  [SITE_SELECTION_SOURCE.PAGE_LOAD]: 'site_selected_via_page_load',
  [SITE_SELECTION_SOURCE.BROWSER_HISTORY]: 'site_selected_via_browser_history',
  [SITE_SELECTION_SOURCE.OTHER]: 'site_selected_via_other',
});

export const siteSelectionEventName = (source) => SITE_SELECTION_EVENTS[source] ?? SITE_SELECTION_EVENTS[SITE_SELECTION_SOURCE.OTHER];

let pendingSiteSelectionSource = null;

export const setSiteSelectionSource = (source) => {
  pendingSiteSelectionSource = source;
};

export const consumeSiteSelectionSource = (fallback = SITE_SELECTION_SOURCE.OTHER) => {
  const source = pendingSiteSelectionSource || fallback;
  pendingSiteSelectionSource = null;
  return source;
};

// Navigate to a site detail page and remember how the user got there (reported as site_selected by SitePage)
export const useOpenSite = () => {
  const navigate = useNavigate();
  return useCallback((siteId, source, options) => {
    setSiteSelectionSource(source);
    navigate(`/sites/${siteId}`, options);
  }, [navigate]);
};
